import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { MdRefresh, MdContentCopy } from "react-icons/md";

import Container from "../../components/Container";
import Loading from "../../../home/components/Loading";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import styles from "./styles.module.scss";

const formatDate = date => (date ? moment(date).format("DD/MM/YYYY HH:mm") : "-");

const formatMoney = value =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const shortKey = chave => (chave ? `${chave.slice(0, 6)}...${chave.slice(-6)}` : "-");

const NfePendingPage = () => {
  window.setPageTitle("NF-e pendentes");

  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/nfe-documents/pending",
        params: { limit: 200 }
      });
      setDocuments(response?.data || response || []);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao carregar as NF-e pendentes."
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const copyKey = async chave => {
    try {
      await navigator.clipboard.writeText(chave);
      Swal.fire({
        icon: "success",
        title: "Chave copiada",
        timer: 1200,
        showConfirmButton: false
      });
    } catch (e) {
      Swal.fire({ icon: "info", title: "Chave da NF-e", text: chave });
    }
  };

  return (
    <>
      <Loading isLoading={loading && !documents.length} />

      <Container title="NF-e pendentes">
        <div className={styles.contentBody}>
          <div className={styles.infoCard}>
            <h5>Notas recebidas sem envio vinculado</h5>
            <p>
              Estas NF-e chegaram pela caixa de e-mail, mas nenhum envio tem a mesma
              chave. Elas ficam fora do CT-e até o vínculo acontecer. Confira se o
              pedido existe e se a chave da nota foi informada no envio.
            </p>
          </div>

          <div className={styles.tableHeader}>
            <h5>
              Pendentes <span className={styles.counter}>{documents.length}</span>
            </h5>
            <button
              type="button"
              className={styles.btnRefresh}
              onClick={loadDocuments}
              title="Atualizar"
            >
              <MdRefresh size={20} />
              <span>Atualizar</span>
            </button>
          </div>

          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="18%">Chave</th>
                  <th width="10%">NF / Série</th>
                  <th width="24%">Emitente</th>
                  <th width="16%">Destino</th>
                  <th width="10%">Valor</th>
                  <th width="10%">Pedido</th>
                  <th width="12%">Recebida em</th>
                </tr>
              </thead>
              <tbody>
                {documents.length > 0
                  ? documents.map(doc => (
                      <tr key={doc.id}>
                        <td>
                          <button
                            type="button"
                            className={styles.keyButton}
                            onClick={() => copyKey(doc.chave)}
                            title={doc.chave}
                          >
                            <span>{shortKey(doc.chave)}</span>
                            <MdContentCopy size={14} />
                          </button>
                        </td>
                        <td>{`${doc.numero} / ${doc.serie}`}</td>
                        <td className={styles.ellipsisCell} title={doc.emitenteNome}>
                          {doc.emitenteNome}
                        </td>
                        <td>{`${doc.destinatarioMunicipio} / ${doc.destinatarioUf}`}</td>
                        <td>{formatMoney(doc.valorNota)}</td>
                        <td>{doc.numeroPedido || "-"}</td>
                        <td>{formatDate(doc.createdAt)}</td>
                      </tr>
                    ))
                  : !loading && (
                      <tr>
                        <td colSpan="7" className={styles.emptyCell}>
                          Nenhuma NF-e pendente. Todas as notas recebidas já estão
                          vinculadas a um envio.
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>
          </div>
        </div>
      </Container>
    </>
  );
};

export default NfePendingPage;
